import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { categoryService } from '@/services/categoryService';
import { getRefreshConfig } from '@/config/refresh.config';

interface CategoryWithChildren {
  id: string | number;
  parentId?: string | number | null;
  sortOrder?: number;
  children: CategoryWithChildren[];
  [key: string]: unknown;
}

function structureCategories(categories: CategoryWithChildren[]): CategoryWithChildren[] {
  if (!Array.isArray(categories)) return [];

  const parentCategories = categories.filter(cat => !cat.parentId);
  const subcategories = categories.filter(cat => cat.parentId);

  const categoryMap = new Map<string | number, CategoryWithChildren>();
  parentCategories.forEach(cat => {
    categoryMap.set(cat.id, { ...cat, children: [] });
  });

  subcategories.forEach(subcat => {
    const parent = categoryMap.get(subcat.parentId as string | number);
    if (parent) {
      parent.children.push(subcat);
    }
  });

  categoryMap.forEach(cat => {
    cat.children.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  });

  return Array.from(categoryMap.values()).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

export const useCategories = (includeChildren = false) => {
  return useQuery({
    queryKey: ['categories', includeChildren],
    queryFn: () => categoryService.list(includeChildren),
    staleTime: 10 * 60 * 1000,
    select: (data: CategoryWithChildren[]) => {
      if (includeChildren) {
        return structureCategories(data);
      }
      return data;
    },
    ...getRefreshConfig('static'),
  });
};

export const useCategoriesHierarchical = () => {
  return useQuery({
    queryKey: ['categories', 'hierarchical'],
    queryFn: () => categoryService.list(true),
    staleTime: 10 * 60 * 1000,
    select: (data: CategoryWithChildren[]) => structureCategories(data),
    ...getRefreshConfig('static'),
  });
};

export const useCategory = (slug: string | undefined) => {
  return useQuery({
    queryKey: ['category', slug],
    queryFn: () => categoryService.getBySlug(slug!),
    enabled: !!slug,
    staleTime: 10 * 60 * 1000,
  });
};

export const useCategoryById = (id: string | number | undefined) => {
  return useQuery({
    queryKey: ['category', 'id', id],
    queryFn: () => categoryService.getById(id!),
    enabled: !!id,
    staleTime: 10 * 60 * 1000,
  });
};

export const useCategoryWithChildren = (id: string | number | undefined) => {
  return useQuery({
    queryKey: ['category', 'with-children', id],
    queryFn: () => categoryService.getWithChildren(id!),
    enabled: !!id,
    staleTime: 10 * 60 * 1000,
  });
};

export const useCategoryChildren = (id: string | number | undefined) => {
  return useQuery({
    queryKey: ['category', 'children', id],
    queryFn: () => categoryService.getChildren(id!),
    enabled: !!id,
    staleTime: 10 * 60 * 1000,
  });
};

export const useCreateCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (categoryData: Record<string, unknown>) => categoryService.create(categoryData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
  });
};

export const useUpdateCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: Record<string, unknown> }) =>
      categoryService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
  });
};

export const useDeleteCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => categoryService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
  });
};
